// Read-only mirror of the parse side of new-doctor-app's
// `src/lib/otcRequestNotes.ts`. When a patient orders via the OTC catalog
// (rather than uploading a prescription image), `Prescription.notes` holds
// a JSON blob prefixed with `OTC_REQUEST_JSON:` instead of free-text notes.
// This app only ever displays it, so only the parser is mirrored here.

export const OTC_REQUEST_JSON_PREFIX = "OTC_REQUEST_JSON:" as const;

export interface ParsedOtcNotes {
  catalogSource: "search" | "write_request" | null;
  recipientDisplay: string;
  deliveryType: string;
  address: string;
  contact: string;
  items: { name: string; strength: string; qty: number }[];
  additionalNotes: string | null;
}

export function parseOtcNotes(notes: unknown): ParsedOtcNotes | null {
  if (typeof notes !== "string") return null;
  if (!notes.startsWith(OTC_REQUEST_JSON_PREFIX)) return null;
  try {
    const p = JSON.parse(
      notes.slice(OTC_REQUEST_JSON_PREFIX.length)
    ) as Record<string, unknown>;
    const recipient = p.recipient as
      | { label?: unknown; phone?: unknown }
      | undefined;
    const delivery = p.delivery as Record<string, unknown> | undefined;
    const items = Array.isArray(p.items) ? p.items : [];
    const cs = p.catalogSource;
    return {
      catalogSource: cs === "search" || cs === "write_request" ? cs : null,
      recipientDisplay: recipient
        ? `${String(recipient.label ?? "N/A")} (${String(recipient.phone ?? "N/A")})`
        : "N/A",
      deliveryType: String(delivery?.type ?? "N/A"),
      address: String(delivery?.addressText ?? "N/A"),
      contact: String(delivery?.contactPhone ?? "N/A"),
      items: items.map((it: Record<string, unknown>) => ({
        name: String(it?.name ?? "Item"),
        strength: it?.strength != null ? String(it.strength) : "",
        qty: Number(it?.quantity) || 0,
      })),
      additionalNotes:
        typeof p.additionalNotes === "string" && p.additionalNotes.trim()
          ? p.additionalNotes.trim()
          : null,
    };
  } catch {
    return null;
  }
}
